import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SettingsContext } from '../../context/SettingsProvider';
import logo from '../../assets/images/DLM-logo-shadow.png';
import imagesData from '../../assets/backgrounds/images.json';
import PasswordStrengthIndicator from '../../components/display/PasswordStrengthIndicator';
import zxcvbn from 'zxcvbn';

const imageUrls = imagesData.images.map((image) =>
  require(`../../assets/backgrounds/${image}`)
);

const validatePassword = (password) => {
  const pattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
  return pattern.test(password);
};

export default function ResetPage() {
  const { resetPassword } = useContext(SettingsContext);
  const [inputs, setInputs] = useState({
    password: '',
    confirmPassword: '',
  });
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [backgroundImageIndex, setBackgroundImageIndex] = useState(0);
  const [opacity, setOpacity] = useState(1);

  useEffect(() => {
    const changeBackground = () => {
      const nextIndex = (backgroundImageIndex + 1) % imageUrls.length;
      setOpacity(0);
      setTimeout(() => {
        setBackgroundImageIndex(nextIndex);
        setOpacity(1);
      }, 500);
    };

    const intervalId = setInterval(changeBackground, 10000);
    return () => clearInterval(intervalId);
  }, [backgroundImageIndex]);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(location.search);
    const token = urlSearchParams.get('token');
    if (token) {
      // Store token securely in localStorage
      localStorage.setItem('resetToken', token);

      // Remove token from URL
      const newUrl = `${window.location.origin}${window.location.pathname}`;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    if (inputs.password !== inputs.confirmPassword) {
      setErrorMessage('Passwords do not match.');
      setIsLoading(false);
      return;
    }

    if (inputs.password) {
      if (!validatePassword(inputs.password)) {
        setErrorMessage(
          'Your password must be at least 8 characters long, and include at least one uppercase letter, one lowercase letter, one number, and one special character.'
        );
        setIsLoading(false);
        return;
      }
      const passwordScore = zxcvbn(inputs.password).score;
      if (passwordScore < 3) {
        setErrorMessage('Password is too weak. Please choose a stronger password.');
        setIsLoading(false);
        return;
      }
    }

    const token = localStorage.getItem('resetToken');
    if (!token) {
      setErrorMessage('Invalid or missing token. Please request a new password reset link.');
      setIsLoading(false);
      return;
    }

    try {
      // Only send the password field
      await resetPassword({ password: inputs.password }, token);
      localStorage.removeItem('resetToken'); // Clear token after successful reset
      setIsLoading(false);
      navigate('/login'); // Redirect to login page
    } catch (err) {
      setErrorMessage('An error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <>
      <div
        className="fixed left-0 top-0 z-0 h-full w-full transition-opacity duration-1000"
        style={{
          backgroundImage: `url(${imageUrls[backgroundImageIndex]})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: opacity,
        }}
      ></div>
      <div className="relative z-10 flex min-h-screen flex-col justify-center bg-opacity-75 py-12 sm:px-6 lg:px-8">
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white px-4 py-8 shadow sm:rounded-lg sm:px-10">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
              <img
                className="w-6/8 mx-auto my-20 mb-8 h-32"
                src={logo}
                alt="Logo"
              />
              <h2 className="mt-6 pb-10 text-center text-2xl font-bold text-gray-400">
                Password Reset
              </h2>
              {errorMessage && (
                <p className="mt-2 text-center text-sm text-red-600">
                  {errorMessage}
                </p>
              )}
            </div>

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  New Password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type={passwordVisible ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                    value={inputs.password}
                    onChange={(e) =>
                      setInputs({ ...inputs, password: e.target.value })
                    }
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="confirm-password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Confirm Password
                </label>
                <div className="mt-1">
                  <input
                    id="confirm-password"
                    name="confirmPassword"
                    type={passwordVisible ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    className="block w-full appearance-none rounded-md border border-gray-400 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                    value={inputs.confirmPassword}
                    onChange={(e) =>
                      setInputs({ ...inputs, confirmPassword: e.target.value })
                    }
                  />
                </div>
                <PasswordStrengthIndicator password={inputs.password} />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="show-password"
                    name="showPassword"
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-400 text-indigo-600 focus:ring-indigo-500"
                    onChange={() => setPasswordVisible(!passwordVisible)}
                  />
                  <label
                    htmlFor="show-password"
                    className="ml-2 block text-sm text-gray-900"
                  >
                    Show password
                  </label>
                </div>
              </div>
              <div>
                <button
                  type="submit"
                  className="flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  disabled={isLoading}
                >
                  {isLoading ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
